import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/landing/Footer";

const pillars = [
  "Financial Sovereignty",
  "Institutional Oversight",
  "Strategic Velocity",
  "Governance Resilience",
];

const modules = [
  {
    name: "Executive Identity & Sovereign Leadership",
    focus: "Narrative, presence, boundaries, strategic communication.",
    outcome: "Hold executive presence under pressure.",
  },
  {
    name: "Governance, Boards & Institutional Structures",
    focus: "Boards, reporting lines, risk oversight, AI and cyber risk.",
    outcome: "Explain fiduciary duty; draft governance memos and minutes.",
  },
  {
    name: "Strategic Decision-Making & Executive Judgment",
    focus: "Scenario planning, risk, crisis leadership, ethics.",
    outcome: "Make and explain a decision under uncertainty.",
  },
  {
    name: "Enterprise Finance & Economic Power",
    focus: "Financial statements, ratios, capital strategy, compensation and equity.",
    outcome: "Take a position on a capital decision.",
  },
  {
    name: "Power Negotiation, Influence & Leadership Systems",
    focus: "Negotiation, stakeholders, team and culture design.",
    outcome: "Run an executive negotiation.",
  },
  {
    name: "Boardroom Simulation, Case Studies & Executive Placement",
    focus: "Simulations, cases, portfolio, placement strategy.",
    outcome: "Leave with an executive portfolio and placement strategy.",
  },
];

const statusLines = [
  "In development; content may change.",
  "Lesson materials go to enrolled participants only.",
  "Not accredited.",
  "Enrollment is not open; no dates or fees set.",
  "No promise of board placement, employment or income.",
];

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } },
});

const eyebrow = "text-[0.65rem] tracking-[0.4em] uppercase text-[#D4AF37] mb-6";

export default function AcceleratorPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "SHE Global™ Boardroom Accelerator | Curriculum Overview";
    window.scrollTo(0, 0);
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.title = prevTitle;
    };
  }, []);

  const goInterest = () => {
    navigate("/");
    setTimeout(() => document.querySelector("#interest")?.scrollIntoView({ behavior: "smooth" }), 600);
  };

  return (
    <div className="bg-[#0A0A0A] text-[#F5F5F5]" data-testid="accelerator-page">
      <div className="grain-overlay" />
      <Navbar />
      <main className="pt-20">
        <section className="relative py-24 lg:py-36" data-testid="accelerator-header">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div variants={fadeUp()} initial="hidden" animate="visible" className="max-w-3xl">
              <p className={eyebrow}>Curriculum Overview</p>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F5]" data-testid="accelerator-heading">
                SHE Global™ Boardroom <em className="italic text-[#D4AF37]">Accelerator</em>
              </h1>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-white" data-testid="accelerator-intro">
                A program in development that prepares women to move from executing decisions to shaping them.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 lg:py-32 bg-[#0E0E0E]" data-testid="accelerator-built">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div variants={fadeUp()} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
              <p className={eyebrow}>How It's Built</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {pillars.map((p, i) => (
                  <motion.div
                    key={p}
                    variants={fadeUp(0.08 + i * 0.1)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                    className="border border-white/10 bg-[#141414] p-8 flex items-end min-h-[160px] hover:border-[#D4AF37]/50 transition-colors duration-500"
                    data-testid={`accelerator-pillar-${i + 1}`}
                  >
                    <h3 className="font-serif-display text-xl text-[#F5F5F5] leading-snug">{p}</h3>
                  </motion.div>
                ))}
              </div>
              <motion.div
                variants={fadeUp(0.2)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="mt-5 border border-[#D4AF37]/40 bg-[#141414] p-10"
                data-testid="accelerator-executive-sovereignty"
              >
                <h3 className="font-serif-display text-2xl text-[#D4AF37]">Executive Sovereignty</h3>
                <p className="mt-3 text-sm leading-relaxed text-white">How she holds herself under pressure.</p>
              </motion.div>
              <motion.p
                variants={fadeUp(0.3)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="mt-14 text-center"
                data-testid="accelerator-thread"
              >
                <span className="block text-[0.65rem] tracking-[0.4em] uppercase text-[#5b5b63] mb-4">One thread</span>
                <span className="font-serif-display italic text-2xl sm:text-3xl text-[#D4AF37]">
                  "There is always something beneath something."
                </span>
              </motion.p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 lg:py-32" data-testid="accelerator-modules">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.p variants={fadeUp()} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className={eyebrow}>
              Modules
            </motion.p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
              {modules.map((m, i) => (
                <motion.div
                  key={m.name}
                  variants={fadeUp(0.06 + i * 0.08)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  whileHover={{ y: -6 }}
                  className="border border-white/10 bg-[#141414] p-8 flex flex-col hover:border-[#D4AF37]/50 transition-colors duration-500"
                  data-testid={`accelerator-module-${i + 1}`}
                >
                  <span className="font-serif-display italic text-3xl text-[#D4AF37]/60">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-serif-display text-xl text-[#F5F5F5] leading-snug">{m.name}</h3>
                  <p className="mt-6 text-[0.65rem] tracking-[0.3em] uppercase text-[#D4AF37]">Focus</p>
                  <p className="mt-2 text-sm leading-relaxed text-white">{m.focus}</p>
                  <p className="mt-5 text-[0.65rem] tracking-[0.3em] uppercase text-[#D4AF37]">Outcome</p>
                  <p className="mt-2 text-sm leading-relaxed text-white">{m.outcome}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24 lg:pb-32" data-testid="accelerator-capstone-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              variants={fadeUp()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="border border-[#D4AF37]/40 bg-[#141414] p-10 lg:p-14"
              data-testid="accelerator-capstone"
            >
              <p className={eyebrow}>Capstone Defense</p>
              <p className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#F5F5F5] max-w-3xl">
                Present and defend a strategic framework before a faculty panel.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="pb-24 lg:pb-32" data-testid="accelerator-status-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              variants={fadeUp()}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="border border-white/10 bg-[#0E0E0E] p-10 lg:p-14 max-w-3xl"
              data-testid="accelerator-status"
            >
              <p className={eyebrow}>Program Status</p>
              <ul className="space-y-4">
                {statusLines.map((line) => (
                  <li key={line} className="flex items-start gap-4 text-sm leading-relaxed text-white">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#D4AF37]" />
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        <section className="pb-28 lg:pb-40 text-center" data-testid="accelerator-cta">
          <motion.button
            variants={fadeUp()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            onClick={goInterest}
            data-testid="accelerator-express-interest-btn"
            className="bg-[#D4AF37] text-black text-xs tracking-[0.3em] uppercase font-semibold px-10 py-5 hover:bg-[#C5A24D] transition-colors duration-300"
          >
            Express Interest
          </motion.button>
        </section>
      </main>
      <Footer />
    </div>
  );
}
